import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API } from '../config';
import { notify, notifyError } from '../notify';
import { th, td, useSort, sortRows, HeaderCell } from './sortTable';
import { Chip, RuleHints } from './subBits';

// Subreddits → Found: subs the sub finder (or the Competitors page) added and
// nobody has scraped yet. Shared by every org and creator. Scrape the good
// ones (rules, flairs and the AI classification come with it; then Discover
// can suggest them), dismiss the rest (the finder won't add them again).

const SCRAPE_USD_PER_SUB = 0.022; // the scraper's measured Claude cost per sub (Sep 2026)
const pill = (active) => ({
  padding: '5px 12px', borderRadius: '20px', border: 'none', cursor: 'pointer', fontSize: '12px', fontWeight: '600',
  background: active ? '#ff4500' : '#2a2a2a', color: active ? '#ffffff' : '#aaaaaa'
});
const num = (v) => (v === null || v === undefined ? '—' : Number(v).toLocaleString());
function age(iso) {
  if (!iso) return '—';
  const days = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  return days < 1 ? 'today' : days === 1 ? 'yesterday' : `${days}d ago`;
}
const noHints = (s) => s.check && s.check.mentions_verification === false && !s.check.mentions_karma && !s.check.mentions_account_age;

const COLUMNS = [
  { key: 'name', label: 'Subreddit', value: s => s.name.toLowerCase(), render: s => <>
    <a href={`https://www.reddit.com/${s.name}/`} target="_blank" rel="noreferrer" style={{ color: '#ff4500', textDecoration: 'none' }}>{s.name}</a>
    {s.is_nsfw ? <Chip color="#ef4444">NSFW</Chip> : <Chip color="#22c55e">SFW</Chip>}
    {s.submission_type === 'self' && <Chip color="#60a5fa">text only</Chip>}
    {s.subreddit_type === 'restricted' && <Chip color="#a78bfa" title="Only approved users can post">✋ Request to Post</Chip>}
  </> },
  { key: 'visitors', label: 'Weekly visitors', value: s => s.weekly_visitors_count, render: s => num(s.weekly_visitors_count) },
  { key: 'contrib', label: 'Contributions', value: s => s.weekly_contributions_count, render: s => num(s.weekly_contributions_count) },
  { key: 'rules', label: 'Rules mention', render: s => <RuleHints check={s.check} /> },
  { key: 'via', label: 'Found by', value: s => s.found_via || '', render: s => <span className="sub" title={s.check && s.check.description ? s.check.description : undefined}>{s.found_via || '—'}</span> },
  { key: 'added', label: 'Added', value: s => (s.created_at ? Date.parse(s.created_at) : null), render: s => age(s.created_at) }
];

export default function FoundSubs({ refreshKey }) {
  const [rows, setRows] = useState(null);
  const [rating, setRating] = useState('all');
  const [onlyOpen, setOnlyOpen] = useState(false);
  const [minVisitors, setMinVisitors] = useState('');
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(new Set());
  const [busy, setBusy] = useState(false);
  const [sort, onSort] = useSort({ key: 'visitors', dir: 'desc' });
  const load = useCallback(() => axios.get(`${API}/api/sub-finder/found`)
    .then(r => setRows(r.data)).catch(err => { notifyError(err, 'Could not load the found subs'); setRows([]); }), []);
  useEffect(() => { load(); }, [load, refreshKey]);
  if (!rows) return <div className="card"><p className="empty">Loading…</p></div>;

  const min = parseInt(minVisitors, 10) || 0;
  const shown = rows.filter(s => (rating === 'all' || (rating === 'nsfw') === !!s.is_nsfw)
    && (!onlyOpen || noHints(s)) && (s.weekly_visitors_count || 0) >= min
    && (!search || s.name.toLowerCase().includes(search.toLowerCase())));
  const picked = shown.filter(s => selected.has(s.id));
  const allPicked = shown.length > 0 && picked.length === shown.length;
  const toggle = (id) => setSelected(prev => { const next = new Set(prev); if (next.has(id)) next.delete(id); else next.add(id); return next; });
  const act = async (path, done) => {
    setBusy(true);
    try {
      const { data } = await axios.post(`${API}/api/sub-finder/found/${path}`, { ids: picked.map(s => s.id) });
      notify(done(data), 'success');
      setSelected(new Set());
      await load();
    } catch (err) { notifyError(err, 'Could not update the subs'); }
    setBusy(false);
  };
  const scrape = () => {
    if (window.confirm(`Scrape ${picked.length} sub(s)? About $${(picked.length * SCRAPE_USD_PER_SUB).toFixed(2)} in AI classification.`)) {
      act('scrape', d => `${d.queued} queued for a scrape`);
    }
  };
  const dismiss = () => {
    if (window.confirm(`Dismiss ${picked.length} sub(s)? They leave the list for everyone and the finder won't add them again.`)) {
      act('dismiss', d => `${d.dismissed} dismissed${d.kept_in_use ? ` (${d.kept_in_use} kept: an account uses them)` : ''}`);
    }
  };

  return (
    <div className="card">
      <div className="form-row" style={{ justifyContent: 'space-between', flexWrap: 'wrap', marginBottom: '10px' }}>
        <h2 style={{ margin: 0 }}>Found, not scraped ({rows.length})</h2>
        <div style={{ display: 'flex', gap: '6px' }}>
          <button className="btn-small" disabled={busy || !picked.length} onClick={scrape}>Scrape{picked.length ? ` ${picked.length}` : ''}</button>
          <button className="btn-danger btn-small" disabled={busy || !picked.length} onClick={dismiss}>Dismiss</button>
        </div>
      </div>
      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center', marginBottom: '10px' }}>
        {['all', 'nsfw', 'sfw'].map(r => <button key={r} style={pill(rating === r)} onClick={() => setRating(r)}>{r === 'all' ? 'All' : r.toUpperCase()}</button>)}
        <button style={pill(onlyOpen)} onClick={() => setOnlyOpen(v => !v)} title="Rules mention no verification, karma or account age: good for new accounts">No requirements found</button>
        <input type="number" min="0" placeholder="Min weekly visitors" value={minVisitors} onChange={e => setMinVisitors(e.target.value)} style={{ flex: 'none', width: '170px', padding: '6px 10px' }} />
        <input placeholder="Search name" value={search} onChange={e => setSearch(e.target.value)} style={{ flex: 'none', width: '170px', padding: '6px 10px' }} />
        <span className="sub">{shown.length} shown · select all shown with the top checkbox</span>
      </div>
      {shown.length === 0 ? <p className="empty">Nothing here. Use "Find subreddits" to search.</p> : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead><tr>
              <th style={th}><input type="checkbox" checked={allPicked} onChange={() => setSelected(allPicked ? new Set() : new Set(shown.map(s => s.id)))} style={{ flex: 'none' }} /></th>
              {COLUMNS.map(c => <HeaderCell key={c.key} col={c} sort={sort} onSort={onSort} />)}
            </tr></thead>
            <tbody>
              {sortRows(shown, sort, COLUMNS).map(s => (
                <tr key={s.id}>
                  <td style={td}><input type="checkbox" checked={selected.has(s.id)} onChange={() => toggle(s.id)} style={{ flex: 'none' }} /></td>
                  {COLUMNS.map(c => <td key={c.key} style={td}>{c.render(s)}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
